/*
 * Candidate-plan review data. Retrieval goes through api.getCandidateReview
 * (the verifier's /v1/runs/:id/candidate). The verifier's diff payload is
 * `unknown` by contract; it is validated against the dashboard's PlanDiff
 * schema and anything that does not fit is surfaced as raw JSON — never
 * silently reshaped.
 *
 * This hook is read-only by architecture, not by omission. The verifier
 * never writes to a git repository (its API has no accept/reject routes),
 * so acceptance is a local operation: `blume-tests accept` or the
 * `accept_candidate_plan` MCP tool, guarded by --expected-scenario-hash.
 * The dashboard's job ends at presenting the review and handing off that
 * command — it must never issue a write request for a candidate decision.
 */
import type { ActionPlan } from '@blume/web-test-model'
import { useEffect, useRef, useState } from 'react'

import { createDashboardApi, DashboardApiError } from '@/api'
import {
  fixtureCandidateReviews,
  PlanDiff,
  type PlanDiff as PlanDiffValue
} from '@/design/fixture-candidates'

import { verifierBaseUrl, type FeedMode } from './use-run-feed'

export type CandidateState =
  | { kind: 'loading' }
  | { kind: 'ready'; candidate: ActionPlan; diff?: PlanDiffValue; rawDiff?: unknown }
  | { kind: 'empty' }
  | { kind: 'unauthorized'; message: string }
  | { kind: 'error'; message: string }

export interface CandidateReviewFeed {
  mode: FeedMode
  state: CandidateState
}

export function useCandidateReview(runId: string): CandidateReviewFeed {
  const base = verifierBaseUrl()
  const mode: FeedMode = base === undefined ? 'fixture' : 'live'
  const [state, setState] = useState<CandidateState>({ kind: 'loading' })
  const disposedRef = useRef(false)

  useEffect(() => {
    disposedRef.current = false

    if (base === undefined) {
      const fixture = fixtureCandidateReviews[runId]
      setState(
        fixture
          ? { kind: 'ready', candidate: fixture.candidate, diff: fixture.diff }
          : { kind: 'empty' }
      )
      return
    }

    setState({ kind: 'loading' })
    const api = createDashboardApi(base)
    void api
      .getCandidateReview(runId)
      .then((review) => {
        if (disposedRef.current) return
        const parsedDiff = PlanDiff.safeParse(review.diff)
        setState({
          kind: 'ready',
          candidate: review.candidate,
          ...(parsedDiff.success
            ? { diff: parsedDiff.data }
            : review.diff === undefined
              ? {}
              : { rawDiff: review.diff })
        })
      })
      .catch((error: unknown) => {
        if (disposedRef.current) return
        if (error instanceof DashboardApiError && (error.status === 401 || error.status === 403)) {
          setState({ kind: 'unauthorized', message: error.message })
        } else if (error instanceof DashboardApiError && error.status === 404) {
          setState({ kind: 'empty' })
        } else {
          setState({
            kind: 'error',
            message: error instanceof Error ? error.message : 'Could not load the candidate plan'
          })
        }
      })
    return () => {
      disposedRef.current = true
    }
  }, [base, runId])

  return { mode, state }
}
